import React, { useEffect, useState } from "react";
import { BellRinging, X, Share, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { getPushState, subscribePush, isIOS, isStandalone } from "@/lib/push";
import { installBannerWouldShow } from "@/components/portal/PortalInstallBanner";

const DISMISS_KEY = "mfg_push_optin_dismissed_at";
const TTL = 7 * 24 * 60 * 60 * 1000;

// Explicit, tap-driven notification opt-in for dispatchers/agents.
export default function PushOptInCard() {
  const [state, setState] = useState(() => getPushState());
  const [subs, setSubs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return !!at && Date.now() - at < TTL;
  });

  // One nudge at a time on phones: let the home-screen banner go first (iOS needs it before push works anyway).
  const [waitForInstall, setWaitForInstall] = useState(() => !isStandalone() && installBannerWouldShow());
  useEffect(() => {
    const onHidden = () => setWaitForInstall(false);
    window.addEventListener("mfg:install-banner-hidden", onHidden);
    return () => window.removeEventListener("mfg:install-banner-hidden", onHidden);
  }, []);

  useEffect(() => {
    api.get("/push/status").then((r) => setSubs(r.data?.subscriptions ?? 0)).catch(() => setSubs(0));
  }, []);

  const iosNeedsInstall = isIOS() && !isStandalone();
  const granted = state.permission === "granted" && subs > 0;
  if (dismissed || waitForInstall || granted || subs === null) return null;
  if (!state.supported && !iosNeedsInstall) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
    setDismissed(true);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await subscribePush();
      setState(getPushState());
      if (r === "granted") {
        setSubs(1);
        toast.success("Alerts on — sending you a test notification");
        api.post("/push/test").catch(() => {});
      } else if (r === "denied") {
        toast.error("Notifications are blocked for this site — allow them in your browser settings");
      } else if (r === "ios-install") {
        toast.info("On iPhone, add MFG to your home screen first, then turn on alerts");
      } else if (r === "unconfigured") {
        toast.error("Push isn't configured on the server yet");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not turn on alerts");
    } finally { setBusy(false); }
  };

  const blocked = state.permission === "denied";
  return (
    <div className="mb-4 rounded-2xl border border-[#0A84FF]/30 bg-[#0A84FF]/5 p-4 relative" data-testid="push-optin-card" data-permission={state.permission}>
      <button onClick={dismiss} aria-label="Dismiss" data-testid="push-optin-dismiss"
        className="absolute top-2 right-2 w-9 h-9 !min-h-0 grid place-items-center text-neutral-400 hover:text-black rounded-md">
        <X size={16} weight="bold" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <div className="w-10 h-10 rounded-lg bg-[#0A84FF] text-white grid place-items-center shrink-0"><BellRinging size={20} weight="fill" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-base leading-tight text-black">Get phone alerts for your loads</div>
          <div className="text-xs text-neutral-600 mt-0.5 leading-relaxed">
            We'll notify you the moment a driver texts back and when a load sits 24 hours with no status change — even with the app closed.
          </div>
          {iosNeedsInstall ? (
            <div className="mt-3 rounded-lg bg-white border border-black/10 px-3 py-2 text-xs text-neutral-700" data-testid="push-optin-ios-hint">
              <Share size={14} weight="bold" className="inline align-text-bottom mr-1" />
              iPhone: tap <strong>Share → Add to Home Screen</strong> first, open MFG from there, then tap <strong>Turn on alerts</strong>.
            </div>
          ) : blocked ? (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 inline-flex items-start gap-1.5" data-testid="push-optin-blocked">
              <WarningCircle size={14} weight="bold" className="shrink-0 mt-0.5" />
              <span>Notifications are blocked for this site. Open your browser's site settings (lock icon → Notifications) and set them to <strong>Allow</strong>, then reload.</span>
            </div>
          ) : (
            <button onClick={enable} disabled={busy} data-testid="push-optin-enable-btn"
              className="mt-3 inline-flex items-center gap-2 bg-[#0A84FF] hover:bg-[#0058B0] text-white px-4 py-2 rounded-md text-sm font-bold disabled:opacity-60">
              <CheckCircle size={14} weight="bold" /> {busy ? "Turning on…" : "Turn on alerts"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
